import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import useOrderUpdates from '@/hooks/useOrderUpdates';
import { UpdatesActions } from './UpdatesActions';
import { UpdatesFilterChips } from './UpdatesFilterChips';
import { UpdatesHeader } from './UpdatesHeader';
import { UpdatesSummarySkeleton } from './UpdatesSummary';
import { UpdatesSummaryCard } from './UpdatesSummaryCard';
import { UpdatesSections } from './UpdatesSections';
import { TimelineSkeleton } from './UpdatesTimelineView';
import useUpdatesTimeline from './useUpdatesTimeline';
import { UpdatesOrderContext } from './updatesContext';

interface OrderUpdatesTabProps {
  orderUid: string;
  customerPhone?: string | null;
  canResolve: boolean;
  showResolve?: boolean;
  onResolve: () => void;
  /** The order's own state, shown above "what happened" (BOXY-715). */
  status?: string | null;
  statusReason?: string | null;
}

const BAND = 'border-0 border-t border-solid border-sd-border px-4 py-4 sm:px-5';

/**
 * BOXY-49, redesigned in BOXY-715 — the order "Updates" tab: a summary card that answers "what
 * happened to my order?" in words, then comments, calls and WhatsApp as three named sections with
 * filter chips. The merged newest-first timeline it replaces made a merchant read every row to
 * find one WhatsApp reply.
 */
export function OrderUpdatesTab({
  orderUid,
  customerPhone,
  canResolve,
  showResolve = true,
  onResolve,
  status,
  statusReason,
}: OrderUpdatesTabProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, isRefreshing, refresh, updatedAt } = useOrderUpdates(orderUid);
  const timeline = useUpdatesTimeline(data);
  const title = String(t('updates_tab'));
  // BOXY-587 — Core's summary replaces the client-side strip; an older backend
  // without it keeps the strip and shows no summary block.
  const summary = data?.summary?.counts ? data.summary : null;

  const loadError = isError ? (
    <div role='alert' className='flex min-w-0 flex-1 flex-wrap items-center gap-3'>
      <span className='text-[13px] text-sd-foreground'>{String(t('updates_load_error'))}</span>
      <Button variant='outline' size='sm' onClick={refresh} disabled={isRefreshing}>
        {String(t('retry'))}
      </Button>
    </div>
  ) : null;

  return (
    <section
      aria-label={title}
      className='w-full overflow-hidden rounded-2xl border border-sd-border bg-sd-card'
    >
      <UpdatesHeader
        title={title}
        updatedAt={data ? updatedAt : 0}
        isRefreshing={isRefreshing}
        onRefresh={refresh}
      />
      <div className={`${BAND} flex flex-col gap-4 md:flex-row md:items-center md:justify-between`}>
        {isLoading ? <UpdatesSummarySkeleton /> : !data ? loadError : null}
        <UpdatesActions
          orderUid={orderUid}
          phone={customerPhone}
          canResolve={canResolve}
          showResolve={showResolve}
          onResolve={onResolve}
        />
      </div>
      {isLoading ? (
        <div className={BAND}>
          <TimelineSkeleton />
        </div>
      ) : data ? (
        <div className={`${BAND} space-y-4`}>
          {loadError}
          {summary ? (
            <UpdatesSummaryCard summary={summary} status={status} statusReason={statusReason} />
          ) : null}
          <UpdatesFilterChips timeline={timeline} />
          <UpdatesOrderContext.Provider value={orderUid}>
            <UpdatesSections timeline={timeline} />
          </UpdatesOrderContext.Provider>
        </div>
      ) : null}
    </section>
  );
}
